import {createContext, useContext, useEffect, useState, useRef} from 'react';
import params from "./data/params.json";
import {ExTranslationGetInfo} from "./ExTranslation";
import {ExGenderGetInfo} from "./ExGender";
import {ExSpellingGetInfo} from "./ExSpelling";

const LessonsContext = createContext(null);
const ExInfoContext = createContext(null);


export function DataProvider({children}) {

    // ***********************  Exercise info content  **********************************************
    //
    // PART THAT CONCERNS THE Exercices
    //
    const exElementGetInfoList = {
        ExSpellingGetInfo,
        ExQCMGetInfo: ExGenderGetInfo,
        ExTranslationGetInfo
    };

    const getExElementGetInfoFunctionList = () => {
        return exElementGetInfoList;
    }

    // Synthetize all the exInfo including stats
    const exInfoList = useRef(
        Object.keys(getExElementGetInfoFunctionList()).reduce((acc, getInfoStr) => {
            let info = getExElementGetInfoFunctionList()[getInfoStr]({});
            info = info.map(i => {
                i['getInfoStr'] = getInfoStr;
                i['getInfo'] = getExElementGetInfoFunctionList()[getInfoStr];
                return i;
            })
            return info.concat(acc);
        }, [])
    )

    const exFilterTexts = (exInfo, choiceIndex, texts) => {
        return exInfo.getInfo({
            action: "filter",
            mode: exInfo?.modes ? exInfo.modes[choiceIndex] : exInfo.group,
            choice: exInfo.choices[choiceIndex],
            choiceIndex: choiceIndex,
            texts: texts
        })
    }


    // ***********************  LessonsContext **********************************************
    //
    // PART THAT CONCERNS THE LESSONS
    // --> Load all the lessons at start with all vocabularies and sentences
    // --> Make them available
    //

    // The bookName useState
    // By default it is "A"
    const [bookName, setBookName] = useState(() => {
        if (localStorage.getItem("bookName")) return localStorage.getItem("bookName");
        localStorage.setItem("bookName", "A")
        return "A"
    })

    // The allLessons useState
    const [allLessons, setAllLessons] = useState(null)

    // All the word categories
    const [allCategories, setAllCategories] = useState(null);

    // A useEffect to load both allLessons and allCategories
    // The only dependance is on bookName
    useEffect(() => {
        // The async function compute the useState allLessons (and allCategories)
        const getAllLessonsAndAllCategories = async () => {

            // A useful function to normalize all the texts
            function reorderHebrewDiacriticsFast(text) {
                return text.replace(/([\u05D0-\u05EA])([\u0591-\u05C7]*)/g, (_, letter, marks) => {
                    if (!marks) return letter;
                    const dagesh = marks.includes('\u05BC') ? '\u05BC' : '';
                    const vowels = marks.replace('\u05BC', '');
                    return letter + dagesh + vowels;
                });
            }

            // First we load the params.json data
            let al = params[bookName].lessons.map((l, index) => ({
                ...l,
                index: index,
                title: l.name,
                wordLevel: 0,
                sentenceLevel: 0,
                bookName: bookName.current,
                content: {}
            }))

            // Then the whole rest
            const promises = al.filter(l => l.file).map(l => import(`./data/${l.file}.json`).then(res => res.default));
            const objList = await Promise.all(promises);

            const allCategoriesSet = new Set();
            let indexPromises = 0;
            al.forEach((l, index) => {
                l.loaded = true;
                if (l.file === null) return;
                let content = Object.keys(objList[indexPromises]).reduce((acc, f) => {
                    acc[f] = (acc[f] ?? []).concat(
                        // This is the text to be processed
                        objList[indexPromises][f]
                            // change the format of texts that are not words
                            .map(t => Array.isArray(t) ? {exData: t} : t)
                            // add some fields to the text
                            .map(t => {
                                t["lesson"] = index;
                                t["type"] = f;
                                return t;
                            })
                    )
                    return acc;
                }, {})
                l.content = content;
                if ('words' in l.content) {
                    l.content.words.forEach(w => {
                        if (Array.isArray(w.cat)) {
                            w.cat.forEach(cat => allCategoriesSet.add(cat));
                        }
                        Object.keys(w.formes).map(form => w.formes[form][3] = reorderHebrewDiacriticsFast(w.formes[form][3]))
                    });
                }
                indexPromises += 1;
            })
            setAllLessons(al)
            setAllCategories([...allCategoriesSet].sort())
        }
        getAllLessonsAndAllCategories();

    }, [bookName])

    // The useState for the course filenames
    const [courseFilenames, setCourseFilenames] = useState(null);

    // The useEffect to load them
    useEffect(() => {
        const loadCourseFilenames = async () => {
            const filenames = await Promise.all(
                allLessons.map(async (l) => {
                    const filename = `/Cours/${l.file}.html`;
                    return fetch(filename)
                        .then(async (response) => {
                            if (!response.ok) return null;
                            const text = await response.text();
                            if (text.includes("course-file")) return filename;
                            else return null;
                        })
                        .catch(() => null);
                })
            );
            setCourseFilenames(filenames);
        };
        if (allLessons) loadCourseFilenames();
    }, [allLessons]);

    //
    // Get the all the texts associated to a lesson
    //
    const getAllTexts = (selectedLesson, flagSingleLesson) => {
        if (!allLessons) return null;
        let selectedLessonsList =
            (selectedLesson < 0 || selectedLesson >= allLessons.length
                ? []
                : (flagSingleLesson === true
                    ? [selectedLesson]
                    : [...Array(selectedLesson + 1).keys()]));
        let flagOnlyWords = false;

        // Case for special lessons
        const selectedLessonsListTrue = [...selectedLessonsList];
        if (selectedLessonsList.length === 1 && allLessons[selectedLesson]?.allwords) {
            flagOnlyWords = true;
            selectedLessonsList = [...Array(allLessons.length).keys()]
        }

        const originalContent = selectedLessonsList.reduce((acc, index) => {
            const lessonContent = allLessons[index].content;
            Object.keys(lessonContent).forEach(key => {
                if (flagOnlyWords && key !== 'words') return;
                acc[key] = (acc[key] ?? []).concat(lessonContent[key]);
            });
            return acc;
        }, {})

        let res = {selectedLessonsList: selectedLessonsListTrue}

        Object.keys(originalContent).forEach(key => {
            res[key] = {
                selectedLessonsList: selectedLessonsListTrue,
                originalContent: originalContent[key],
            }
        })
        return res;
    }


    //
    // Some usefule functions
    //

    // A useful function to flatten texts
    const flattenTexts = (texts) => {
        if (texts.length === 0) return [];
        return (texts[0].type === 'words'
            ? texts.flatMap(word => Object.values(word.formes))
            : (texts[0].type === 'themes' || texts[0].type === 'versions'
                ? texts.map(t => t.exDataList)
                : texts.reduce((acc, t) => acc.concat([t.exData]), [])))
    }

    // Given a list of words we merge the mergeable words in the list using the mergekey
    const mergeWords = (words) => {
        let mergedWords = {}
        words.forEach(w => {
            if ("mergekey" in w) {
                if (!(w['mergekey'] in mergedWords)) {
                    mergedWords[w['mergekey']] = {
                        formes: {},
                        cat: w.cat,
                        lesson: w.lesson,
                        merge: w.mergekey,
                        type: "words"
                    }
                    if ('genre' in w) mergedWords[w['mergekey']].genre = w['genre']
                } else mergedWords[w['mergekey']].lesson = -1;
                mergedWords[w['mergekey']].formes = {...mergedWords[w['mergekey']].formes, ...w.formes}
            }
        })
        return Object.values(mergedWords);
    }

    //
    // Defining the two contexts : Lesson and ExInfo
    //
    return (
        <LessonsContext.Provider value={{
            allLessons,
            allCategories,
            getAllTexts,
            flattenTexts,
            courseFilenames,
            mergeWords,
            bookName,
            setBookName
        }}>
            <ExInfoContext.Provider value={{exInfoList, exFilterTexts}}>
                {children}
            </ExInfoContext.Provider>
        </LessonsContext.Provider>
    );
}

export function useLessons() {
    return useContext(LessonsContext);
}

export function useExInfo() {
    return useContext(ExInfoContext);
}

